import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, Check, Search } from "lucide-react";
import {
  Badge,
  Button,
  FormField,
  Input,
  Label,
} from "@/design-system/components/ui";
import { Select, Textarea } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import { brokersApi } from "@/api/brokers";
import type { BrokerSummaryDto } from "@/api/brokers";
import { brokerPickerLabel } from "@/lib/brokerDisplay";
import { cn } from "@/design-system/lib/utils";
import { useCreateDeal, useDealTypeHint } from "@/hooks/deals/useDeals";
import { brokerKeys } from "@/hooks/brokers/brokerKeys";
import {
  DEAL_TYPE_LABEL,
  type CreateDealRequest,
  type DealType,
  type DealTypeHintDto,
  type TransactionStatus,
} from "@/types/deals";
import type { SelectedParty } from "@/components/parties/PartyPicker";
import { DealPartyLookup } from "./newDeal/DealPartyLookup";
import { DealBusinessLookup, type SelectedBusiness } from "./newDeal/DealBusinessLookup";

interface Props {
  open: boolean;
  onClose: () => void;
  /** When launched from a broker context, prefill + skip the Broker step
   *  (the broker stays changeable via "Change broker"). */
  initialBrokerId?: string | null;
  initialBrokerName?: string | null;
}

interface Draft {
  brokerId: string | null;
  brokerName: string | null;
  business: SelectedBusiness | null;
  borrower: SelectedParty | null;
  seller: SelectedParty | null;
  purchasePrice: string;
  sellerNoteAmount: string;
  transactionStatus: TransactionStatus | "";
  targetCloseDate: string;
  dealNarrative: string;
  /** Owner Recapitalization Loan (surface U1) — the staff choice. */
  dealType: DealType;
}

const EMPTY_DRAFT: Draft = {
  brokerId: null,
  brokerName: null,
  business: null,
  borrower: null,
  seller: null,
  purchasePrice: "",
  sellerNoteAmount: "",
  transactionStatus: "",
  targetCloseDate: "",
  dealNarrative: "",
  // A draft saved before the deal type existed merges to Acquisition.
  dealType: "Acquisition",
};

const DEAL_TYPES = Object.keys(DEAL_TYPE_LABEL) as DealType[];

const RECAP_SAME_PARTY_ERROR =
  "On an Owner Recapitalization the Borrower and the Seller must be different parties.";

const DRAFT_KEY = "phoenix.deal-wizard.draft";
const AUTOSAVE_MS = 30_000;

const STEPS = ["Broker", "Initial info", "Confirm"] as const;
type Step = (typeof STEPS)[number];

const TRANSACTION_STATUS_OPTIONS: { value: TransactionStatus; label: string }[] = [
  { value: "PendingLOI", label: "Pending LOI" },
  { value: "LOISigned", label: "LOI signed" },
  { value: "PASigned", label: "PA signed" },
  { value: "InClosing", label: "In closing" },
  { value: "Other", label: "Other" },
];

/**
 * New Deal Wizard. Three steps: Broker selection → Initial deal info → Confirm.
 *
 * Counterparty sunset: Borrower/Seller are Party-first and the subject
 * Business is its own entity — all three are REQUIRED lookups (with inline
 * create-new) captured here, and attached to the deal at create time
 * (borrowerPartyId / sellerPartyId / businessId). A deal can be created for a
 * broker in ANY status (no certification gate). When launched from a broker,
 * that broker is prefilled and the Broker step is skipped (still changeable).
 *
 * Auto-save persists the draft to localStorage on every change and every 30s;
 * reopening with a stored draft prompts to resume or discard.
 */
export function NewDealWizard({ open, onClose, initialBrokerId, initialBrokerName }: Props) {
  const navigate = useNavigate();
  const toast = useToast();
  const create = useCreateDeal();

  const [step, setStep] = useState<Step>("Broker");
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [resumePromptShown, setResumePromptShown] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<number | null>(null);

  // Owner Recapitalization Loan (surfaces U1, U4) — a Seller converted from an
  // owner recapitalization prequalification makes the deal Owner
  // Recapitalization: the server applies that rule at create (and writes the
  // activity row), so the wizard only shows the type it will have.
  const hintQuery = useDealTypeHint(open ? draft.seller?.id : null);
  const recapHint =
    draft.seller && hintQuery.data?.isOwnerRecapitalizationSeller === true
      ? hintQuery.data
      : null;
  const effectiveType: DealType = recapHint ? "OwnerRecapitalization" : draft.dealType;

  // On open: prefill a launch-context broker (skip the Broker step), else
  // surface the resume / discard prompt for a saved draft.
  useEffect(() => {
    if (!open) return;
    setError(null);
    if (initialBrokerId) {
      setDraft({ ...EMPTY_DRAFT, brokerId: initialBrokerId, brokerName: initialBrokerName ?? null });
      setStep("Initial info");
      setResumePromptShown(false);
      return;
    }
    setStep("Broker");
    const stored = readDraft();
    if (stored && hasContent(stored)) {
      setDraft(stored);
      setResumePromptShown(true);
    } else {
      setDraft(EMPTY_DRAFT);
      setResumePromptShown(false);
    }
  }, [open, initialBrokerId, initialBrokerName]);

  // Periodic auto-save while the modal is open.
  useEffect(() => {
    if (!open) return;
    intervalRef.current = window.setInterval(() => {
      writeDraft(draft);
    }, AUTOSAVE_MS);
    return () => {
      if (intervalRef.current) window.clearInterval(intervalRef.current);
    };
  }, [open, draft]);

  // Always persist the latest snapshot when state changes.
  useEffect(() => {
    if (open) writeDraft(draft);
  }, [draft, open]);

  function update<K extends keyof Draft>(key: K, value: Draft[K]) {
    setDraft((d) => ({ ...d, [key]: value }));
  }

  async function submit() {
    setError(null);
    if (!draft.brokerId) {
      setError("Choose a broker first.");
      setStep("Broker");
      return;
    }
    if (!draft.business || !draft.borrower || !draft.seller) {
      setError("Business, Borrower, and Seller are all required.");
      setStep("Initial info");
      return;
    }
    // The Initial info step states this error itself.
    if (isSamePartyOnRecap(draft, effectiveType)) {
      setStep("Initial info");
      return;
    }
    const purchasePrice = Number(draft.purchasePrice);
    const sellerNote = Number(draft.sellerNoteAmount || 0);
    if (!Number.isFinite(purchasePrice) || purchasePrice <= 0) {
      setError("Purchase price must be a positive number.");
      setStep("Initial info");
      return;
    }
    if (!Number.isFinite(sellerNote) || sellerNote < 0) {
      setError("Seller note amount must be 0 or positive.");
      setStep("Initial info");
      return;
    }

    const payload: CreateDealRequest = {
      brokerId: draft.brokerId,
      purchasePrice,
      sellerNoteAmount: sellerNote,
      businessId: draft.business.id,
      // Carry the selected business's name so the deal's display name stays
      // meaningful (the backend folds BusinessNameInitial into DisplayName).
      businessNameInitial: draft.business.name,
      borrowerPartyId: draft.borrower.id,
      sellerPartyId: draft.seller.id,
      transactionStatus: draft.transactionStatus || null,
      targetCloseDate: draft.targetCloseDate || null,
      dealNarrative: draft.dealNarrative || null,
      // The staff choice, not the type shown for a recap Seller: the server
      // applies the auto-set and records it against the prequalification.
      dealType: draft.dealType,
    };
    try {
      const deal = await create.mutateAsync(payload);
      toast.success(`Created deal ${deal.dealNumber}.`);
      clearDraft();
      onClose();
      navigate(`/pipeline/${deal.id}`);
    } catch (err) {
      const msg =
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : err instanceof Error
            ? err.message
            : "Could not create deal.";
      setError(msg);
    }
  }

  function discard() {
    clearDraft();
    setDraft(EMPTY_DRAFT);
    setResumePromptShown(false);
  }

  const stepIndex = STEPS.indexOf(step);
  const isLast = step === "Confirm";

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      size="lg"
      title="New deal"
      description="Attach the broker, business, borrower, and seller, then create the deal."
      footer={
        <>
          <Button variant="tertiary" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          {stepIndex > 0 && (
            <Button
              variant="secondary"
              onClick={() => setStep(STEPS[stepIndex - 1])}
              disabled={create.isPending}
            >
              <ArrowLeft size={14} aria-hidden /> Back
            </Button>
          )}
          {!isLast ? (
            <Button
              onClick={() => setStep(STEPS[stepIndex + 1])}
              disabled={!canAdvance(step, draft, effectiveType)}
            >
              Next <ArrowRight size={14} aria-hidden />
            </Button>
          ) : (
            <Button onClick={submit} loading={create.isPending}>
              <Check size={14} aria-hidden /> Create deal
            </Button>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-space-4">
        <Stepper current={step} />

        {resumePromptShown && (
          <ResumePrompt
            onResume={() => setResumePromptShown(false)}
            onDiscard={() => {
              discard();
            }}
          />
        )}

        {step === "Broker" && (
          <BrokerStep
            draft={draft}
            onSelect={(broker) => {
              update("brokerId", broker.id);
              update("brokerName", broker.companyName);
            }}
          />
        )}
        {step === "Initial info" && (
          <InitialInfoStep
            draft={draft}
            effectiveType={effectiveType}
            recapHint={recapHint}
            onChange={update}
            onChangeBroker={() => setStep("Broker")}
          />
        )}
        {step === "Confirm" && <ConfirmStep draft={draft} effectiveType={effectiveType} />}

        {error && (
          <p className="text-small text-status-error" role="alert">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}

function canAdvance(step: Step, draft: Draft, effectiveType: DealType): boolean {
  if (step === "Broker") return !!draft.brokerId;
  if (step === "Initial info") {
    const price = Number(draft.purchasePrice);
    return (
      !!draft.business &&
      !!draft.borrower &&
      !!draft.seller &&
      Number.isFinite(price) &&
      price > 0 &&
      !isSamePartyOnRecap(draft, effectiveType)
    );
  }
  return true;
}

function isSamePartyOnRecap(draft: Draft, effectiveType: DealType): boolean {
  return (
    effectiveType === "OwnerRecapitalization" &&
    !!draft.borrower &&
    !!draft.seller &&
    draft.borrower.id === draft.seller.id
  );
}

/** Both amounts entered and different (surface U1, OP13: a warning only). */
function priceAndNoteDiffer(draft: Draft): boolean {
  if (draft.purchasePrice.trim() === "" || draft.sellerNoteAmount.trim() === "") return false;
  const price = Number(draft.purchasePrice);
  const note = Number(draft.sellerNoteAmount);
  return Number.isFinite(price) && Number.isFinite(note) && price !== note;
}

function hasContent(draft: Draft): boolean {
  // The deal type always holds a value; only a choice other than the default
  // makes a draft worth resuming.
  return (Object.keys(draft) as (keyof Draft)[]).some((key) =>
    key === "dealType"
      ? draft.dealType !== EMPTY_DRAFT.dealType
      : draft[key] !== null && draft[key] !== "",
  );
}

function isDealType(value: unknown): value is DealType {
  return typeof value === "string" && (DEAL_TYPES as string[]).includes(value);
}

function readDraft(): Draft | null {
  try {
    const raw = window.localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    // Merge over EMPTY_DRAFT so a draft persisted under an older shape still
    // gets defaults; stale fields are ignored by the typed reads below.
    const stored = JSON.parse(raw) as Partial<Draft>;
    const merged = { ...EMPTY_DRAFT, ...stored };
    // An unknown deal type token falls back to the default.
    return isDealType(merged.dealType) ? merged : { ...merged, dealType: EMPTY_DRAFT.dealType };
  } catch {
    return null;
  }
}

function writeDraft(draft: Draft) {
  try {
    window.localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  } catch {
    /* localStorage may be unavailable in private browsing — silent. */
  }
}

function clearDraft() {
  try {
    window.localStorage.removeItem(DRAFT_KEY);
  } catch {
    /* see writeDraft */
  }
}

// ----------------------------------------------------------------------
// Sub-components
// ----------------------------------------------------------------------

function Stepper({ current }: { current: Step }) {
  return (
    <ol className="flex items-center gap-space-2 m-0 p-0 list-none">
      {STEPS.map((s, i) => {
        const active = s === current;
        const done = STEPS.indexOf(current) > i;
        return (
          <li
            key={s}
            className={cn(
              "flex items-center gap-space-2 text-label font-display uppercase tracking-label",
              active ? "text-onyx" : done ? "text-status-success" : "text-gray-mid",
            )}
          >
            <span
              className={cn(
                "inline-flex items-center justify-center w-5 h-5 rounded-full text-caption font-bold",
                active && "bg-gold text-onyx",
                done && "bg-status-success text-white-pure",
                !active && !done && "bg-white-warm text-gray-mid",
              )}
            >
              {i + 1}
            </span>
            <span>{s}</span>
            {i < STEPS.length - 1 && <span aria-hidden>·</span>}
          </li>
        );
      })}
    </ol>
  );
}

function ResumePrompt({ onResume, onDiscard }: { onResume: () => void; onDiscard: () => void }) {
  return (
    <div className="flex items-center justify-between gap-space-3 p-space-3 border border-gold rounded-md bg-gold/5">
      <span className="text-small text-onyx">
        We found an in-progress draft. Resume where you left off, or discard it.
      </span>
      <div className="flex items-center gap-space-2">
        <Button variant="tertiary" onClick={onDiscard}>
          Discard
        </Button>
        <Button variant="secondary" onClick={onResume}>
          Resume
        </Button>
      </div>
    </div>
  );
}

function BrokerStep({
  draft,
  onSelect,
}: {
  draft: Draft;
  onSelect: (broker: BrokerSummaryDto) => void;
}) {
  const [search, setSearch] = useState(draft.brokerName ?? "");
  const brokersQuery = useQuery({
    queryKey: ["brokers", "wizard", search],
    queryFn: () =>
      brokersApi.list({
        page: 1,
        pageSize: 25,
        q: search || undefined,
        view: "Pipeline",
      }),
    staleTime: 30_000,
  });
  // A deal can be created for a broker in ANY status (no certification gate) —
  // surface all pipeline brokers, showing each broker's status.
  const rows = brokersQuery.data?.data ?? [];

  return (
    <div className="flex flex-col gap-space-3">
      <FormField label="Search brokers" required>
        <div className="relative">
          <Input
            placeholder="Name, contact, state…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Search
            size={14}
            className="absolute right-space-2 top-1/2 -translate-y-1/2 text-gray-mid"
            aria-hidden
          />
        </div>
      </FormField>

      {brokersQuery.isLoading && (
        <div className="text-small text-gray-mid italic">Searching…</div>
      )}

      {!brokersQuery.isLoading && rows.length === 0 && (
        <p className="text-small text-gray-mid">
          No brokers match. Onboard one from the Brokers area, then come back to
          start the deal.
        </p>
      )}

      <ul className="flex flex-col gap-space-2 m-0 p-0 list-none max-h-[320px] overflow-y-auto">
        {rows.map((b) => {
          const selected = b.id === draft.brokerId;
          const { primary, secondary } = brokerPickerLabel(b);
          return (
            <li key={b.id}>
              <button
                type="button"
                onClick={() => onSelect(b)}
                className={cn(
                  "w-full flex items-center justify-between gap-space-3",
                  "px-space-3 py-space-2 border rounded-md text-left",
                  "transition hover:border-gold",
                  selected
                    ? "border-gold bg-gold/10 text-onyx"
                    : "border-border-light bg-white-pure",
                )}
              >
                <div className="min-w-0">
                  <div className="font-semibold text-small truncate">{primary}</div>
                  <div className="text-caption text-gray-mid truncate">
                    {secondary || (b.state ?? "—")}
                  </div>
                </div>
                <div className="flex items-center gap-space-2 shrink-0">
                  <Badge status={b.status === "Certified" ? "finalized" : "info"}>
                    {b.status}
                  </Badge>
                  {selected && <Check size={16} className="text-status-success" aria-hidden />}
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function InitialInfoStep({
  draft,
  effectiveType,
  recapHint,
  onChange,
  onChangeBroker,
}: {
  draft: Draft;
  effectiveType: DealType;
  recapHint: DealTypeHintDto | null;
  onChange: <K extends keyof Draft>(key: K, value: Draft[K]) => void;
  onChangeBroker: () => void;
}) {
  // Resolve the broker name for display when the wizard was launched with a
  // broker id but no name (e.g. from a broker's Deals tab).
  const brokerQuery = useQuery({
    queryKey: brokerKeys.detail(draft.brokerId ?? ""),
    queryFn: () => brokersApi.get(draft.brokerId!),
    enabled: !!draft.brokerId && !draft.brokerName,
    staleTime: 60_000,
  });
  const brokerName = draft.brokerName ?? brokerQuery.data?.companyName ?? "—";
  const isRecap = effectiveType === "OwnerRecapitalization";
  const recapReference =
    recapHint?.prequalificationReference?.trim() || recapHint?.prequalificationId || "";

  return (
    <div className="flex flex-col gap-space-3">
      <div className="flex items-center justify-between gap-space-3 rounded-md border border-border-light bg-white-warm px-space-3 py-space-2">
        <div className="min-w-0">
          <div className="font-display text-caption uppercase tracking-label text-gray-mid">
            Broker
          </div>
          <div className="text-small text-onyx truncate">{brokerName}</div>
        </div>
        <button
          type="button"
          onClick={onChangeBroker}
          className="shrink-0 text-caption uppercase tracking-wide text-gold-shadow hover:underline"
        >
          Change broker
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3">
        <div className="md:col-span-2">
          <DealTypeControl
            value={effectiveType}
            onChange={(v) => onChange("dealType", v)}
            // A recap Seller makes the deal Owner Recapitalization whatever is
            // chosen here (the server's auto-set), so Acquisition is not offered.
            acquisitionDisabled={recapHint !== null}
          />
        </div>
        {recapHint && (
          <p
            role="status"
            className="md:col-span-2 m-0 rounded-md border border-gold bg-gold/5 px-space-3 py-space-2 text-small text-onyx"
          >
            This seller completed an owner recapitalization prequalification ({recapReference}),
            so Deal type is set to Owner Recapitalization.
          </p>
        )}

        <div className="md:col-span-2">
          <DealBusinessLookup
            label="Business"
            value={draft.business}
            onChange={(v) => onChange("business", v)}
          />
          {isRecap && (
            <FieldHelper>The operating business, as the new company will own it.</FieldHelper>
          )}
        </div>
        <div>
          <DealPartyLookup
            type="Borrower"
            label="Borrower"
            value={draft.borrower}
            onChange={(v) => onChange("borrower", v)}
          />
          {isRecap && <FieldHelper>The new company the owners form (the Buyer).</FieldHelper>}
        </div>
        <div>
          <DealPartyLookup
            type="Seller"
            label="Seller"
            value={draft.seller}
            onChange={(v) => onChange("seller", v)}
          />
          {isRecap && (
            <FieldHelper>The existing company that owns the business today (the Seller).</FieldHelper>
          )}
        </div>

        <FormField
          label="Purchase price"
          required
          htmlFor="price"
          helperText={isRecap ? "Equals the amount financed." : undefined}
        >
          <Input
            id="price"
            type="number"
            inputMode="decimal"
            value={draft.purchasePrice}
            onChange={(e) => onChange("purchasePrice", e.target.value)}
            placeholder="0"
          />
        </FormField>

        <FormField
          label="Seller note amount"
          htmlFor="seller-note"
          helperText={isRecap ? "Equals the purchase price." : undefined}
        >
          <Input
            id="seller-note"
            type="number"
            inputMode="decimal"
            value={draft.sellerNoteAmount}
            onChange={(e) => onChange("sellerNoteAmount", e.target.value)}
            placeholder="0"
          />
        </FormField>

        {isRecap && priceAndNoteDiffer(draft) && (
          <p role="status" className="md:col-span-2 m-0 text-small text-status-warning">
            On an Owner Recapitalization the seller note amount normally equals the purchase price.
          </p>
        )}

        <FormField label="Transaction status" htmlFor="tx-status">
          <Select
            id="tx-status"
            value={draft.transactionStatus}
            onChange={(e) =>
              onChange("transactionStatus", e.target.value as TransactionStatus | "")
            }
          >
            <option value="">Select…</option>
            {TRANSACTION_STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </FormField>

        <FormField label="Target close date" htmlFor="close-date">
          <Input
            id="close-date"
            type="date"
            value={draft.targetCloseDate}
            onChange={(e) => onChange("targetCloseDate", e.target.value)}
          />
        </FormField>

        <FormField label="Deal narrative" className="md:col-span-2" htmlFor="narrative">
          <Textarea
            id="narrative"
            rows={4}
            value={draft.dealNarrative}
            onChange={(e) => onChange("dealNarrative", e.target.value)}
            placeholder="Brief storytelling — what's the deal, why interesting?"
          />
        </FormField>

        {isSamePartyOnRecap(draft, effectiveType) && (
          <p role="alert" className="md:col-span-2 m-0 text-small text-status-error">
            {RECAP_SAME_PARTY_ERROR}
          </p>
        )}
      </div>
    </div>
  );
}

/** Owner Recapitalization Loan (surface U1) — the Deal type segmented control,
 *  styled as the Pipeline view toggle. */
function DealTypeControl({
  value,
  onChange,
  acquisitionDisabled,
}: {
  value: DealType;
  onChange: (value: DealType) => void;
  acquisitionDisabled: boolean;
}) {
  const labelId = useId();
  const helperId = useId();
  const base =
    "h-8 px-space-3 inline-flex items-center text-label font-display font-bold uppercase tracking-button transition-colors duration-fast ease-standard disabled:cursor-not-allowed disabled:opacity-40";
  return (
    <div className="flex flex-col">
      <Label id={labelId} required>
        Deal type
      </Label>
      <div
        role="group"
        aria-labelledby={labelId}
        aria-describedby={helperId}
        className="inline-flex self-start rounded-md border border-border-light bg-white-pure overflow-hidden"
      >
        {DEAL_TYPES.map((type) => (
          <button
            key={type}
            type="button"
            aria-pressed={value === type}
            disabled={type === "Acquisition" && acquisitionDisabled}
            onClick={() => onChange(type)}
            className={cn(
              base,
              value === type
                ? "bg-gold text-onyx hover:bg-gold-highlight"
                : "bg-transparent text-gray-mid hover:text-onyx hover:bg-gold-highlight/10",
            )}
          >
            {DEAL_TYPE_LABEL[type]}
          </button>
        ))}
      </div>
      <p id={helperId} className="text-label text-gray-mid mt-[6px]">
        Owner Recapitalization: the owners&apos; new company is the Borrower and their existing
        company is the Seller and Note Holder. You can change this until the deal enters
        Pre-Approval.
      </p>
    </div>
  );
}

/** A field helper line, styled as the shared FormField's helper text. */
function FieldHelper({ children }: { children: ReactNode }) {
  return <p className="text-label text-gray-mid mt-[6px]">{children}</p>;
}

function ConfirmStep({ draft, effectiveType }: { draft: Draft; effectiveType: DealType }) {
  // Resolve the broker name for display when the wizard was launched with a
  // broker id but no name (id-only prefill or an autosaved-draft resume).
  const brokerQuery = useQuery({
    queryKey: brokerKeys.detail(draft.brokerId ?? ""),
    queryFn: () => brokersApi.get(draft.brokerId!),
    enabled: !!draft.brokerId && !draft.brokerName,
    staleTime: 60_000,
  });
  const resolvedBrokerName = brokerQuery.data?.companyName ?? null;

  const fmt = (n: string) =>
    n
      ? new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
          maximumFractionDigits: 0,
        }).format(Number(n))
      : "—";
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-space-4 gap-y-space-2 text-small">
      <Row label="Deal type" value={DEAL_TYPE_LABEL[effectiveType]} />
      <Row label="Broker" value={draft.brokerName ?? resolvedBrokerName ?? "—"} />
      <Row label="Business" value={draft.business?.name ?? "—"} />
      <Row label="Borrower" value={draft.borrower?.name ?? "—"} />
      <Row label="Seller" value={draft.seller?.name ?? "—"} />
      <Row label="Purchase price" value={fmt(draft.purchasePrice)} />
      <Row label="Seller note" value={fmt(draft.sellerNoteAmount)} />
      <Row label="Transaction status" value={draft.transactionStatus || "—"} />
      <Row label="Target close date" value={draft.targetCloseDate || "—"} />
      <Row label="Narrative" value={draft.dealNarrative || "—"} full />
    </div>
  );
}

function Row({ label, value, full }: { label: string; value: string; full?: boolean }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <div className="font-display text-caption uppercase tracking-label text-gray-mid">
        {label}
      </div>
      <div className="text-onyx">{value}</div>
    </div>
  );
}
